export type PollOptions = {
  intervalMs: number;
  timeoutMs: number;
  message?: string;
};

export interface ControlledPromise<T> {
  promise: Promise<T>;
  resolve: (x: T) => void;
  reject: (err: unknown) => void;
}

/**
 * A convenient wrapper around the Promise constructor.
 */
export function createPromise<T>(): ControlledPromise<T> {
  let resolve!: (x: T) => void;
  let reject!: (err: unknown) => void;
  const promise = new Promise<T>((r, e) => ([resolve, reject] = [r, e]));
  return { promise, resolve, reject };
}

const defaultPollOptions: PollOptions = {
  intervalMs: 15,
  timeoutMs: 5 * 60e3,
};

// Returns a promise that resolves when `callback` returns true or rejects if
// the timeout is reached. The value of `callback` is rechecked at a
// configurable interval.
export function poll(
  callback: () => Promise<boolean> | boolean,
  callerOptions: Partial<PollOptions> = {},
): Promise<void> {
  const options = { ...defaultPollOptions, ...callerOptions };

  const timedOut = (async (): Promise<never> => {
    await wait(options.timeoutMs);
    const reason = options.message ? `: ${options.message}` : '';
    throw new Error(`Timed out after polling for ${options.timeoutMs}ms${reason}`);
  })();

  const pollFulfilled = (async (): Promise<void> => {
    while (true) {
      const result = callback();
      const success = typeof result === 'boolean' ? result : await result;

      if (success) {
        break;
      }

      await wait(options.intervalMs);
    }
  })();

  return Promise.race([timedOut, pollFulfilled]).catch((error: unknown) => {
    // Explicitly log, and then rethrow, the error for browsers that don't support unhandledrejection.
    console.error(`Uncaught in poll(): ${error}`);
    throw error;
  });
}

// Returns a Promise that resolves when the 'load' event fires on an Element,
// and rejects if the 'error' event fires. To avoid races in cases where these
// events can fire synchronously, a `startLoading()` callback can be provided to
// trigger loading after the event listeners have been registered.
export async function loadEvent(elem: Element, startLoading?: () => void): Promise<void> {
  const loadEvent = new Promise<void>((resolve, reject) => {
    const onLoad = (): void => {
      elem.removeEventListener('error', onError);
      resolve();
    };
    const onError = (): void => {
      elem.removeEventListener('load', onLoad);
      reject(new Error(`Got error while loading ${elem.outerHTML}`));
    };
    elem.addEventListener('load', onLoad, { once: true });
    elem.addEventListener('error', onError, { once: true });
  });

  if (startLoading) {
    startLoading();
  }
  return loadEvent;
}

// Returns a Promise that resolves after 'ms' milliseconds.
export function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

type CountTo = {
  targetReached: Promise<number>;
  incCount(by?: number): void;
};

// Returns 'incCount()', a function that increases a counter, and
// 'targetReached', a Promise that resolves when the counter has reached
// 'target'.
export function countTo(target: number, initial: number = 0): CountTo {
  let counter: number = initial;
  let incCount: CountTo['incCount'] = () => {};

  const targetReached = new Promise<number>(resolve => {
    incCount = (by: number = 1) => {
      counter += by;
      if (counter >= target) {
        resolve(counter);
      }
    };
  });

  return { targetReached, incCount };
}

export function block<V>(
  this: unknown,
  fn: (...args: any[]) => Promise<V>,
): (...args: any[]) => Promise<V | null> {
  let blocking = false;
  return (...args: any[]): Promise<V | null> => {
    if (blocking) {
      return Promise.resolve(null);
    }
    blocking = true;
    const promise = fn.apply(this, args);
    if (!promise) {
      return Promise.resolve(null);
    }
    promise
      .then(() => {
        blocking = false;
      })
      .catch(() => {
        blocking = false;
      });
    return promise;
  };
}

export class CancelAsync extends Error {
  constructor(msg?: string) {
    super(msg);
    Object.setPrototypeOf(this, CancelAsync.prototype);
  }

  isCancelAsync = true;
}

export interface BreakablePromise<T> {
  promise: () => Promise<T>;
  cancel: () => void;
}

// breakable wraps any promise with the ability to break out by calling cancel
// on the returned BreakablePromise. Calling cancel rejects the promise with a
// CancelAsync error. The cancel function is safe to call multiple times or
// even if the wrapped promise has resolved or rejected.
export function breakable<T>(p: Promise<T>): BreakablePromise<T> {
  let broken = false;
  let cancelFn: () => void;
  const wrappedPromise = new Promise<T>((resolve, reject) => {
    cancelFn = () => {
      if (!broken) {
        broken = true;
        reject(new CancelAsync('Promise canceled'));
      }
    };
    p.then((result: T) => {
      if (!broken) {
        broken = true;
        resolve(result);
      }
    }).catch(err => {
      if (!broken) {
        broken = true; // Prevent rejecting a promise that has already rejected by calling cancel
        reject(err);
      }
    });
  });
  return {
    promise: () => wrappedPromise,
    cancel: cancelFn!,
  };
}

export class CancelableWait {
  private _cancelled = false;
  constructor(private _ms: number) {}

  public start(): Promise<void> {
    return new Promise<void>((resolve, reject) =>
      setTimeout(() => {
        if (this._cancelled) {
          reject(new Error('Cancelled'));
        } else {
          resolve();
        }
      }, this._ms),
    );
  }

  public cancel(): void {
    this._cancelled = true;
  }
}

/** Returns the value of a settled promise or else undefined if the promise was rejected. */
export const getPromiseSettledResultValue = <T>(promise: PromiseSettledResult<T>) => {
  if (promise.status === 'fulfilled') {
    return (promise as PromiseFulfilledResult<T>).value;
  }
  return undefined;
};
